import classNames from "classnames";
import Form, { Field, FormInstance, List } from "rc-field-form";
import type { NamePath, Rule, RuleObject } from "rc-field-form/es/interface";
import * as React from "react";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaBadge from "../kuika-badge/kuika-badge";
import KuikaButton, { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../kuika-button/kuika-button";
import KuikaEditorHeader from "../kuika-editor-header/kuika-editor-header";
import KuikaListPage from "../kuika-list-page/kuika-list-page";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaH4 from "../typography/H4/H4";
import KuikaSmallText from "../typography/SmallText/SmallText";
import { shakeInvalidFields } from "./form-shake";
import "./kuika-form-page.scss";

/**
 * Editor page for a multi-field form: the editor bar with the commit pair, one or more
 * `KuikaFormSection`s, and an optional right-hand rail. Layout counterpart of
 * `KuikaListPage` — that one owns a tool's list, this one the page you land on from it —
 * and it stands on the same ground: the body under the bar IS a `KuikaListPage belowBar`,
 * the Scheduled Jobs editor's page, so the two editors cannot drift in gutter or surface.
 *
 * A page rather than a modal: these forms are worked through, some carry a parameter
 * table or a code editor, and a dialog would scroll its own labels away.
 */

/** Outcome of a `verify` run. `ok` picks the chip's colour, `message` is shown verbatim. */
export interface KuikaFormPageVerifyResult {
  ok: boolean;
  message: string;
}

export interface KuikaFormPageVerify {
  label: string;
  onVerify: () => void;
  isVerifying?: boolean;
  /** `null`/absent means "not tried yet" — the chip stays hidden rather than neutral. */
  result?: KuikaFormPageVerifyResult | null;
  testId?: string;
  /** Test id for the result chip. Keep stable: suites assert the outcome, not the button. */
  resultTestId?: string;
}

/** The type's own guide, opened in a new tab from the rail's "Learn more" block. */
export interface KuikaFormPageLink {
  label: string;
  href: string;
  testId?: string;
}

interface KuikaFormPageValidateErrorField {
  errors: string[];
}

interface KuikaFormPageValidateError {
  errorFields?: KuikaFormPageValidateErrorField[];
}

/**
 * The enclosing form's `name`, so a field can scope its control id as
 * `<formName>_<path>` — the shape antd's `Form.Item` generated. Two forms on one page
 * would otherwise mint the same id, and the designer's E2E page objects address inputs
 * by exactly that id.
 */
const KuikaFormNameContext: React.Context<string | undefined> = React.createContext<string | undefined>(undefined);

/**
 * The enclosing `KuikaFormList`'s path. `rc-field-form`'s `List` gives its children a name
 * relative to itself, so without this a row's field would spell its id as `<form>_0_key`
 * instead of `<form>_extraHeaderParameters_0_key`.
 */
const KuikaFormListNameContext: React.Context<(string | number)[]> = React.createContext<(string | number)[]>([]);

/** The first message a rejected submit carries, in field order. */
const firstValidationMessage = (error: unknown): string | undefined => {
  const typed: KuikaFormPageValidateError = error as KuikaFormPageValidateError;
  const field: KuikaFormPageValidateErrorField | undefined = typed?.errorFields?.find(
    (candidate: KuikaFormPageValidateErrorField): boolean =>
      Array.isArray(candidate.errors) && candidate.errors.length > 0
  );
  return field?.errors?.[0];
};

export interface KuikaFormAsideBlockProps {
  /**
   * The block's caption — "Resolves to", "Used by". Omit it only for a block that is itself a
   * labelled field (a Description textarea): the field's own label names it, and a caption
   * above that label would say the same word twice.
   */
  title?: string;
  testId?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * One block of `KuikaFormPage`'s rail. The rail draws the rule between blocks; the block
 * owns only its caption and the rhythm under it, so every form's rail reads the same.
 */
export const KuikaFormAsideBlock = ({
  title,
  testId,
  className,
  children
}: KuikaFormAsideBlockProps): React.ReactElement => (
  <div className={classNames("kuika-form-page__aside-block", className)} data-testid={testId}>
    {title && <h6 className="kuika-form-page__aside-title">{title}</h6>}
    {children}
  </div>
);

export interface KuikaFormPageProps {
  /** Heading copy — name the target, e.g. "New SAP connection". */
  title: string;
  /**
   * Replaces the heading node with a caller-owned control — an inline rename, say. Pass
   * `title` either way: an input nested inside a heading is not announced as its name.
   */
  renderTitle?: (title: string) => React.ReactNode;
  /** Form name; must be unique among mounted forms. */
  formName: string;
  formRef?: React.RefObject<FormInstance>;
  onFinish: (values: Record<string, never>) => void;
  /**
   * Same shape `rc-field-form` hands over. `Record<string, never>` as elsewhere here: the
   * store's fields are the caller's business, so a caller narrows what it reads.
   */
  onValuesChange?: (changedValues: Record<string, never>, values: Record<string, never>) => void;
  submitLabel: string;
  /** Dims the page and blocks its controls — use for save/load, not for `verify`. */
  isLoading?: boolean;
  /**
   * The commit's stateful status (`KuikaEditorHeader`/`KuikaButton`): rolls the label to
   * "Saving"/"Saved"/"Try again" and the button's width follows. Omit it and `isLoading` works
   * exactly as before — when given, `isLoading` is ignored for the commit button.
   */
  status?: KuikaButtonStatus;
  statusLabels?: IKuikaButtonStatusLabels;
  onCancel: () => void;
  /** Left-hand footer action that reaches the target WITHOUT saving the form. */
  verify?: KuikaFormPageVerify;
  /** `KuikaFormSection`s. */
  children: React.ReactNode;
  /**
   * Right-hand rail: what the fields add up to and what depends on them — build it from
   * `KuikaFormAsideBlock`s. Divided by a rule, not its own frame. `verify`, `learnMore` and
   * `afterCreate` each draw a rail as well, in a fixed order around these blocks.
   */
  aside?: React.ReactNode;
  /** The type's guide, as the rail's "Learn more" block — after the caller's blocks. */
  learnMore?: KuikaFormPageLink;
  /**
   * What CREATE does beyond saving — "Tables are read automatically after save." Pass it
   * only while creating: an UPDATE does none of it. Always the rail's last block.
   */
  afterCreate?: string;
  className?: string;
  submitTestId?: string;
  cancelTestId?: string;
}

interface KuikaFormPageState {
  /** Measured once, then held as a floor — see `componentDidMount`. */
  minBodyHeight?: number;
}

export class KuikaFormPage extends React.PureComponent<KuikaFormPageProps, KuikaFormPageState> {
  private ownFormRef = React.createRef<FormInstance>();

  private bodyEl = React.createRef<HTMLDivElement>();

  private rootEl = React.createRef<HTMLDivElement>();

  constructor(props: KuikaFormPageProps) {
    super(props);
    this.state = {};
  }

  // A mode switch swaps six fields for one pasted string, which would otherwise collapse
  // the sheet and move every control under the pointer. The first render is the field
  // mode, so its height is the floor to hold.
  componentDidMount(): void {
    const height: number | undefined = this.bodyEl.current?.offsetHeight;
    if (height) {
      this.setState({ minBodyHeight: height });
    }
  }

  private get formRef(): React.RefObject<FormInstance> {
    return this.props.formRef ?? this.ownFormRef;
  }

  // The commit button sits in the heading, outside the <form>, so it cannot be a native
  // submit — it drives the form instance's own submit, keeping validation in one place.
  handleSubmitClick = (): void => {
    this.formRef.current?.submit();
  };

  private renderTitle(): React.ReactNode {
    if (this.props.renderTitle) {
      return this.props.renderTitle(this.props.title);
    }
    return <KuikaH4>{this.props.title}</KuikaH4>;
  }

  /** In the rail when there is one, in the heading row when there is not. */
  private renderVerify(inRail: boolean): React.ReactNode {
    const { verify } = this.props;
    if (!verify) return null;

    const button: React.ReactNode = (
      <KuikaButton
        type="secondary"
        size="md"
        className="kuika-form-page__verify-button"
        onClick={verify.onVerify}
        loading={verify.isVerifying}
        data-testid={verify.testId}
      >
        <KuikaSvg name="player-play" width={15} height={16} />
        {verify.label}
      </KuikaButton>
    );

    // A driver-level failure can be a paragraph: it wraps in the rail, where there is
    // height for it, and stays one ellipsised line in the heading row (full text on hover).
    // The test-id AND the machine-readable outcome both live on this wrapper — a plain DOM
    // node this component owns outright — rather than on KuikaBadge, which deliberately
    // renders no `...rest` spread (see kuika-badge.tsx). `data-status` mirrors the vocabulary
    // the old data-source-modal.tsx used ("success" | "failed") so a caller can assert the
    // outcome directly instead of guessing it from badge colour or from the backend's
    // driver-specific message text, which is not a stable assertion target.
    const result: React.ReactNode = verify.result && (
      <span
        className={classNames("kuika-form-page__verify-result", {
          "kuika-form-page__verify-result--wrap": inRail
        })}
        title={verify.result.message}
        data-testid={verify.resultTestId}
        data-status={verify.result.ok ? "success" : "failed"}
      >
        <KuikaBadge
          // Wrapped in a span rather than passed as a bare string: the badge is an
          // inline-flex row, so `text-overflow` only bites on an element of its own.
          content={<span className="kuika-form-page__verify-text">{verify.result.message}</span>}
          leftIcon={<KuikaSvg name={verify.result.ok ? "check" : "x"} width={12} height={12} />}
          color={verify.result.ok ? "green" : "red"}
        />
      </span>
    );

    if (!inRail) {
      return (
        <>
          {button}
          {result}
        </>
      );
    }

    return (
      <KuikaFormAsideBlock title="Connection" className="kuika-form-page__verify-block">
        {button}
        {result}
      </KuikaFormAsideBlock>
    );
  }

  private renderLearnMore(): React.ReactNode {
    const { learnMore } = this.props;
    if (!learnMore) return null;

    return (
      <KuikaFormAsideBlock title="Learn more">
        {/* A new tab: the guide is read beside the half-filled form, not instead of it. */}
        <a
          className="kuika-form-page__link"
          href={learnMore.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={learnMore.testId}
        >
          <KuikaSvg type="sidebar-icons" name="book" width={16} height={16} />
          <span className="kuika-form-page__link-text">{learnMore.label}</span>
          <KuikaSvg name="external-link" width={14} height={14} />
        </a>
      </KuikaFormAsideBlock>
    );
  }

  private renderAfterCreate(): React.ReactNode {
    const { afterCreate } = this.props;
    if (!afterCreate) return null;

    return (
      <KuikaFormAsideBlock title="After you create">
        <KuikaSmallText className="kuika-form-page__after-create">{afterCreate}</KuikaSmallText>
      </KuikaFormAsideBlock>
    );
  }

  private handleSubmitFailed = (error: unknown): void => {
    // A field whose error is drawn as nothing but a red edge (a table cell) would
    // otherwise leave the form refusing to save without saying why.
    const message: string | undefined = firstValidationMessage(error);
    KMainFunctions.displayWarningNotification(message ?? "Please correct the highlighted fields.");
    // The invalid fields shake once and the first takes focus. A frame later, so the error classes
    // the failed rules paint are on the page first.
    requestAnimationFrame((): void => {
      if (this.rootEl.current) {
        shakeInvalidFields(this.rootEl.current);
      }
    });
  };

  /**
   * The rail. It does not fold: a form's rail is two or three blocks the fields are read
   * against, so the control that folded it only put a button on the divider.
   *
   * The order is fixed, so every form reads the same way down: can I reach it (verify),
   * what the fields add up to (the caller's blocks), where to read more, and what CREATE
   * does next.
   */
  private renderAside(): React.ReactNode {
    return (
      <aside className="kuika-form-page__aside">
        {this.renderVerify(true)}
        {this.props.aside}
        {this.renderLearnMore()}
        {this.renderAfterCreate()}
      </aside>
    );
  }

  render() {
    // Verify keeps the rail alive on its own: it applies in every mode, while a caller's
    // blocks come and go. Tied to `aside` alone, the button hopped on a mode toggle. The
    // shell's other two blocks are just as steady, so they count too.
    const hasRail: boolean =
      Boolean(this.props.aside) ||
      Boolean(this.props.verify) ||
      Boolean(this.props.learnMore) ||
      Boolean(this.props.afterCreate);

    return (
      <div
        ref={this.rootEl}
        className={classNames("kuika-form-page", this.props.className)}
        aria-busy={this.props.isLoading}
      >
        {/* The form wraps the bar as well as the sheet: the bar can hold a field of its own
            (an inline rename), and a field outside the form's context silently binds to
            nothing — it renders, accepts typing, and never reaches the store. */}
        <Form
          name={this.props.formName}
          ref={this.formRef}
          onFinish={this.props.onFinish}
          onFinishFailed={this.handleSubmitFailed}
          onValuesChange={this.props.onValuesChange}
          className="kuika-form-page__form"
          // SHAKES_ITSELF_ATTRIBUTE: its failed submits shake from `onFinishFailed` above, so the
          // document listener stays out (the test pins the two spellings together).
          data-kuika-shakes-itself=""
        >
          <KuikaFormNameContext.Provider value={this.props.formName}>
            {/* Verify keeps its place beside the commit pair when no rail exists to hold it. */}
            <KuikaEditorHeader
              title={this.props.title}
              renderTitle={this.props.renderTitle ? () => this.renderTitle() : undefined}
              leading={hasRail ? undefined : this.renderVerify(false)}
              submitLabel={this.props.submitLabel}
              onSubmit={this.handleSubmitClick}
              onCancel={this.props.onCancel}
              isLoading={this.props.isLoading}
              status={this.props.status}
              statusLabels={this.props.statusLabels}
              cancelTestId={this.props.cancelTestId}
              submitTestId={this.props.submitTestId}
            />

            {/* The bar is the column's first row; everything under it is the Scheduled Jobs
                ground — a `KuikaListPage belowBar`, which owns the gutter and the scroll. No
                card on it: a `--dark-0` card on the `--dark-1` page read as a hole in dark. */}
            <KuikaListPage belowBar className="kuika-form-page__scroll">
              <div className="kuika-form-page__sheet">
                <div
                  className={classNames("kuika-form-page__body", { "kuika-form-page__body--with-rail": hasRail })}
                  ref={this.bodyEl}
                  style={{ minHeight: this.state.minBodyHeight }}
                >
                  <div className="kuika-form-page__fields">{this.props.children}</div>
                  {hasRail && this.renderAside()}
                </div>
              </div>
            </KuikaListPage>
          </KuikaFormNameContext.Provider>
        </Form>
      </div>
    );
  }
}

export default KuikaFormPage;

export interface KuikaFormFieldProps {
  /** Path in the form store, e.g. `["parsedConnectionString", "Client"]`. */
  name: NamePath;
  /** Omit for a control that a caption already names, such as a table cell. */
  label?: string;
  /** Sits after the label text — a tooltip, a help link; anything the label needs beside it. */
  labelSuffix?: React.ReactNode;
  /**
   * An action drawn at the far end of the label's line, OUTSIDE the `<label>` — "Forgot password?".
   * `labelSuffix` renders inside the label, where a button is invalid HTML (a label may hold no
   * other labelable element) and a click would also focus the field.
   *
   * In the DOM it comes AFTER the control, and only the grid lifts it onto the label's line: Tab
   * from the field above must land in this field, not on its action — otherwise what the person
   * types next goes nowhere and Enter presses the action instead of submitting.
   */
  labelAction?: React.ReactNode;
  /**
   * `false` hides the required marker. Only for a form where EVERY field is required, such as
   * sign-in: marking all of them is noise, and there is no optional field to confuse them with.
   * A form that mixes required and optional fields keeps the marker.
   */
  requiredMarker?: boolean;
  /** `data-testid` on the error line, for a test that must tell "refused" from "not yet answered". */
  errorTestId?: string;
  rules?: Rule[];
  /**
   * Which prop carries the value. `"checked"` for a switch or a checkbox, whose state is
   * not a `value`; anything else stays on `value`.
   */
  valuePropName?: string;
  /** Static hint under the control — a format, a unit. Errors replace nothing; both show. */
  extra?: React.ReactNode;
  className?: string;
  /**
   * One control. It receives `value`/`onChange` from the form, plus `error` and an `id`. An
   * `onChange` of its own still runs, after the form has taken the value (as antd's Form.Item did).
   */
  children: React.ReactElement;
}

/** The one prop of the control the field reads before cloning over it. */
interface KuikaFormFieldChildProps {
  onChange?: (...args: unknown[]) => void;
}

/**
 * One labelled field bound to the form store. The label, the required marker, the error
 * and the hint are all drawn here rather than by a form library, so the markup and its
 * tokens stay ours; `rc-field-form` supplies only the store and the validation.
 */
export const KuikaFormField = ({
  name,
  label,
  labelSuffix,
  labelAction,
  requiredMarker = true,
  errorTestId,
  rules,
  valuePropName,
  extra,
  className,
  children
}: KuikaFormFieldProps) => {
  // The marker follows the rules, so a field cannot claim to be optional while refusing
  // to submit empty.
  const isRequired: boolean = requiredMarker && (rules ?? []).some((rule) => (rule as RuleObject).required === true);
  const formName: string | undefined = React.useContext(KuikaFormNameContext);
  const listName: (string | number)[] = React.useContext(KuikaFormListNameContext);
  const path: (string | number)[] = Array.isArray(name) ? [...name] : [name];
  // `<formName>_<path>`, antd's shape — see `KuikaFormNameContext`. Filtered on emptiness
  // rather than truthiness: a row's index is a number, and `0` is falsy, so the first row
  // of every repeater used to drop it and share one id with a top-level field of that name.
  const id: string = [formName, ...listName, ...path]
    .filter((part: string | number | undefined): boolean => part !== undefined && part !== null && part !== "")
    .join("_");

  // A `checked` control (switch, checkbox) has no `error` prop of its own — its invalid state
  // reads from the frame around it (`kuika-form-field--error` above) — so cloning one in would
  // leak as an unknown DOM attribute the way `error={false}` used to before this same guard.
  const canTakeError: boolean = valuePropName !== "checked";
  // Cloning the store's onChange over the control's would silently drop the control's own.
  const childOnChange: KuikaFormFieldChildProps["onChange"] = (children.props as KuikaFormFieldChildProps).onChange;

  return (
    <Field name={name} rules={rules} valuePropName={valuePropName}>
      {(control, meta) => {
        const error: string | undefined = meta.errors[0];
        // An IDREF list is space-separated, and a path segment may hold a space ("data source").
        const errorId: string = `${id.replace(/\s+/g, "-")}-error`;
        const labelElement: React.ReactNode = label ? (
          <label
            htmlFor={id}
            className={classNames("kuika-form-field__label", {
              "kuika-form-field__label--required": isRequired
            })}
          >
            {label}
            {labelSuffix}
          </label>
        ) : null;
        return (
          <div
            className={classNames(
              "kuika-form-field",
              {
                "kuika-form-field--error": Boolean(error),
                "kuika-form-field--with-label-action": Boolean(labelAction)
              },
              className
            )}
          >
            {labelElement}
            {/* `error` only for a control that declares the prop: a `checked` control (switch,
                checkbox) does not, and would otherwise forward `error={true}` to the DOM as an
                unknown attribute. `id` goes to every control either way — KuikaSwitch and
                KuikaCheckbox forward it to their focusable element, so `htmlFor` above resolves.
                The same controls get the error announced: `aria-invalid` plus `aria-describedby`
                pointing at the line below, which is otherwise only seen, never read out. */}
            {React.cloneElement(children, {
              ...control,
              ...(childOnChange
                ? {
                    onChange: (...args: unknown[]): void => {
                      control.onChange(...args);
                      childOnChange(...args);
                    }
                  }
                : {}),
              id,
              ...(error && canTakeError ? { error: true, "aria-invalid": true, "aria-describedby": errorId } : {})
            })}
            {/* After the control in tab order, on the label's line on screen — see `labelAction`. */}
            {labelAction && <div className="kuika-form-field__label-action">{labelAction}</div>}
            {extra && <span className="kuika-form-field__extra">{extra}</span>}
            {error && (
              <span id={errorId} className="kuika-form-field__error" data-testid={errorTestId}>
                {error}
              </span>
            )}
          </div>
        );
      }}
    </Field>
  );
};

export interface KuikaFormListProps {
  name: NamePath;
  children: React.ComponentProps<typeof List>["children"];
}

/**
 * `rc-field-form`'s `List`, plus the one thing it does not give its children: its own path.
 * A row's `KuikaFormField` needs it to spell the control id the way antd's `Form.Item` did.
 */
export const KuikaFormList = ({ name, children }: KuikaFormListProps): React.ReactElement => {
  const parent: (string | number)[] = React.useContext(KuikaFormListNameContext);
  const path: (string | number)[] = Array.isArray(name) ? [...name] : [name];

  return (
    <KuikaFormListNameContext.Provider value={[...parent, ...path]}>
      <List name={name}>{children}</List>
    </KuikaFormListNameContext.Provider>
  );
};

export interface KuikaFormSectionProps {
  /**
   * What the group is for, in the reader's words — "Endpoint", "Credentials". Inside a
   * `KuikaFormPage` the page numbers it (01, 02…) from CSS, so a group a mode hides drops
   * out of the count instead of leaving a gap.
   */
  legend: string;
  /** One line directly under the legend: a unit, a scope note, where the values go. */
  hint?: string;
  /** Two columns pair naturally-short fields side by side; long values stay at one. */
  columns?: 1 | 2;
  /** A mode switch (a `KuikaTabs`) shown above the fields — fields, or a pasted string. */
  mode?: React.ReactNode;
  /**
   * Names the group for the E2E suite, which asserts these groups exist. The hint gets
   * `${testId}Hint`, since it is the group's explanatory line and is asserted separately.
   */
  testId?: string;
  children: React.ReactNode;
  className?: string;
}

export const KuikaFormSection = ({
  legend,
  hint,
  columns = 1,
  mode,
  testId,
  children,
  className
}: KuikaFormSectionProps) => (
  <section className={classNames("kuika-form-section", className)} data-testid={testId}>
    {/* The hint sits under the legend, not across the row from it: at the far end of a wide
        sheet it was read as belonging to nothing. */}
    <div className="kuika-form-section__legend">
      <h6>{legend}</h6>
      {hint && (
        <KuikaSmallText className="kuika-form-section__hint" data-testid={testId ? `${testId}Hint` : undefined}>
          {hint}
        </KuikaSmallText>
      )}
    </div>
    {mode && <div className="kuika-form-section__mode">{mode}</div>}
    <div
      className={classNames("kuika-form-section__fields", {
        "kuika-form-section__fields--two": columns === 2
      })}
    >
      {children}
    </div>
  </section>
);
